import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';
import Image from '@plone/volto/components/theme/Image/Image';
import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';

import {
  ESCALA_PALCO,
  getGaleriaImageProps,
  type GaleriaFoto,
} from './helpers';

interface PalcoProps {
  foto: GaleriaFoto;
  /** Só para o rótulo das setas dizer de quantas fotos se trata. */
  total: number;
  onAnterior: () => void;
  onProxima: () => void;
  onAmpliar: () => void;
  /** O lápis do editor, quando há um. */
  acoes?: React.ReactNode;
}

/**
 * Foto grande da galeria.
 *
 * O botão de ampliar é a própria área da foto (`inset-0`), e as setas são
 * irmãs dele, não filhas: aninhar botão dentro de botão é HTML inválido e faria
 * a seta disparar o lightbox junto. O véu com número e legenda fica por cima
 * dos dois com `pointer-events: none`, para o clique atravessar até a foto.
 */
export const Palco: React.FC<PalcoProps> = ({
  foto,
  total,
  onAnterior,
  onProxima,
  onAmpliar,
  acoes,
}) => {
  const imageProps = getGaleriaImageProps(foto, ESCALA_PALCO);

  return (
    <div className="mpf-galeria__palco">
      <button
        type="button"
        onClick={onAmpliar}
        aria-label={`Ampliar a foto ${foto.numero} de ${total}`}
        className="mpf-galeria__palco-zoom"
      >
        {imageProps && (
          <Image
            /* `key` por foto: trocar de foto remonta a imagem, e é a
               montagem que dispara a animação de entrada do SCSS. */
            key={foto.key}
            {...imageProps}
            alt={foto.alt}
            /* A foto em cartaz é o conteúdo principal do bloco: adiar o
               carregamento dela só produziria um buraco na primeira pintura.
               As miniaturas e a grade, essas sim, são `lazy`. */
            loading="eager"
            decoding="async"
            className="mpf-galeria__palco-img"
          />
        )}
      </button>

      <div className="mpf-galeria__palco-setas">
        <MPFIconButton
          icon={ArrowLeftIcon}
          label="Foto anterior"
          shape="square"
          onClick={onAnterior}
          className="mpf-galeria__seta"
        />
        <MPFIconButton
          icon={ArrowRightIcon}
          label="Próxima foto"
          shape="square"
          onClick={onProxima}
          className="mpf-galeria__seta"
        />
      </div>

      {acoes}

      {/* O véu só existe com legenda: sem ela seria um gradiente escuro sobre
          a foto sem nada escrito. A numeração da foto em cartaz continua no
          contador do cabeçalho, no rótulo dos botões e no anúncio da live
          region — só saiu de cima da imagem. */}
      {foto.legenda && (
        <div key={foto.key} className="mpf-galeria__palco-veu">
          <p className="mpf-galeria__palco-legenda">{foto.legenda}</p>
        </div>
      )}
    </div>
  );
};

export default Palco;
